import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { 
  sendEmail, 
  sendWelcomeEmail, 
  sendAlertEmail, 
  sendSyncFailureEmail, 
  sendGDPRConfirmationEmail,
  sendPasswordResetEmail, 
  sendSummaryEmail 
} from '@/lib/email';

/**
 * Test Email API Endpoint - All Scenarios
 * POST /api/test-email-all
 * 
 * Envía todos los tipos de email de prueba para validación
 */

export default withAuth(async (req: NextApiRequest, res: NextApiResponse, user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  // Only allow admins
  if ((user as any).rol !== 'admin') {
    return res.status(403).json({ error: 'Solo administradores pueden enviar emails de prueba' });
  }

  const { to = 'gabrielcustodiooliveira@gmail.com' } = req.body;
  const results: Array<{ type: string; success: boolean; id?: string; error?: string }> = [];

  try {
    // 1. Test básico con logo
    console.log('[Test Email] Enviando email básico...');
    const basicResult = await sendEmail({
      to,
      subject: '✅ 1/6 - Email Básico con Logo - Externaliza2',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h1 style="color: #1babe4;">Test Básico</h1>
          <p>Este es un email de prueba básico para verificar que el logo y la configuración funcionan.</p>
          <p><strong>Fecha:</strong> ${new Date().toLocaleString('es-ES')}</p>
        </div>
      `,
    });
    results.push({ type: 'Básico con Logo', ...basicResult });

    // 2. Welcome email
    console.log('[Test Email] Enviando email de bienvenida...');
    const welcomeResult = await sendWelcomeEmail(to, {
      name: 'Gabriel Custodio',
      role: 'admin',
    });
    results.push({ type: 'Bienvenida (Admin)', ...welcomeResult });

    // 3. Alerta CRÍTICA
    console.log('[Test Email] Enviando alerta crítica...');
    const alertCriticalResult = await sendAlertEmail(to, {
      severity: 'critical',
      message: 'Se ha detectado una anomalía crítica en el sistema de sincronización con Cegid ERP. Se requiere atención inmediata del administrador.',
      rule_name: 'Sync Cegid - Fallo de Conexión',
      created_at: new Date().toISOString(),
    });
    results.push({ type: 'Alerta CRÍTICA', ...alertCriticalResult });

    // 4. Alerta MEDIA
    console.log('[Test Email] Enviando alerta media...');
    const alertMediumResult = await sendAlertEmail(to, {
      severity: 'medium',
      message: 'Se han detectado 3 documentos pendientes de revisión por más de 48 horas.',
      rule_name: 'Documentos Pendientes',
      created_at: new Date().toISOString(),
    });
    results.push({ type: 'Alerta MEDIA', ...alertMediumResult });

    // 5. Alerta BAJA
    console.log('[Test Email] Enviando alerta baja...');
    const alertLowResult = await sendAlertEmail(to, {
      severity: 'low',
      message: 'Recordatorio: El informe mensual está disponible para revisión.',
      rule_name: 'Recordatorio de Informe',
      created_at: new Date().toISOString(),
    });
    results.push({ type: 'Alerta BAJA', ...alertLowResult });

    // 6. Fallo de sincronización
    console.log('[Test Email] Enviando notificación de fallo de sync...');
    const syncFailResult = await sendSyncFailureEmail(to, {
      companyCode: 'EXT-001',
      error: 'Connection timeout after 30000ms. Unable to reach Cegid ERP API endpoint.',
      syncRunId: 'sync-' + Date.now(),
    });
    results.push({ type: 'Fallo Sync Cegid', ...syncFailResult });

    // 7. GDPR - Exportación
    console.log('[Test Email] Enviando confirmación GDPR export...');
    const gdprExportResult = await sendGDPRConfirmationEmail(to, 'export');
    results.push({ type: 'GDPR - Exportar Datos', ...gdprExportResult });

    // 8. GDPR - Eliminación
    console.log('[Test Email] Enviando confirmación GDPR delete...');
    const gdprDeleteResult = await sendGDPRConfirmationEmail(to, 'deletion');
    results.push({ type: 'GDPR - Eliminar Cuenta', ...gdprDeleteResult });

    // 9. Reset de contraseña
    console.log('[Test Email] Enviando reset de contraseña...');
    const resetResult = await sendPasswordResetEmail(
      to,
      'https://externaliza2.gabsdev.com/auth/reset?token=demo-token-12345'
    );
    results.push({ type: 'Reset de Contraseña', ...resetResult });

    // 10. Resumen diario
    console.log('[Test Email] Enviando resumen diario...');
    const summaryResult = await sendSummaryEmail(to, {
      period: 'daily',
      metrics: {
        newDocuments: 24,
        processedByAI: 18,
        alerts: 3,
        syncStatus: 'partial',
      },
    });
    results.push({ type: 'Resumen Diario', ...summaryResult });

    // 11. Resumen semanal
    console.log('[Test Email] Enviando resumen semanal...');
    const weeklyResult = await sendSummaryEmail(to, {
      period: 'weekly',
      metrics: {
        newDocuments: 156,
        processedByAI: 142,
        alerts: 8,
        syncStatus: 'success',
      },
    });
    results.push({ type: 'Resumen Semanal', ...weeklyResult });

    // Calcular estadísticas
    const successCount = results.filter(r => r.success).length;
    const failedCount = results.length - successCount;

    return res.status(200).json({
      success: failedCount === 0,
      summary: {
        total: results.length,
        enviados: successCount,
        fallidos: failedCount,
        destinatario: to,
      },
      results: results.map((r, i) => ({
        numero: i + 1,
        tipo: r.type,
        status: r.success ? '✅ ENVIADO' : '❌ FALLO',
        emailId: r.id,
        error: r.error,
      })),
    });

  } catch (error: any) {
    console.error('[API test-email-all] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error interno del servidor',
      results,
    });
  }
});
